import { createSelector } from '@reduxjs/toolkit';
import {
    selectAccaBoostBonusDetails,
    selectAccaBoostLadder,
    selectAccaBoostMinLegs,
    selectAccaBoostMarkets,
    selectAccaBoostMinOddsPerLeg,
} from '../bonusesSliceSelectors';
import {
    selectCurrencyIso,
    selectOddsFormat,
} from '../userSliceSelectors';
import {
    selectSelectionDetails,
    selectCountSelectionDetailsWithoutCombinations,
} from '~Betslip/selectors/selectionDetailsSelectors';
import {
    ACCA_BOOST_TYPE_ID,
    AMERICAN_ODDS_FORMAT,
    FRACTION_ODDS_FORMAT,
} from '~shared/utils/constants';

export const selectAccaBoostPercentage = createSelector(
    selectAccaBoostLadder,
    selectAccaBoostMinOddsPerLeg,
    selectAccaBoostMarkets,
    selectSelectionDetails,
    (ladder, minOddsPerLeg, markets, selections) => {
        const selectionCount = Object.keys(selections).length;
        const isAccaBoostEligible = Object.values(selections).every(({ decimal_price, market_id }) =>
            markets.includes(market_id) && Number(decimal_price) >= Number(minOddsPerLeg.decimal)
        );

        if (!isAccaBoostEligible) {
            return null;
        }

        const lastLegKey = Object.keys(ladder).pop();

        if (selectionCount > lastLegKey) {
            return parseFloat(ladder[lastLegKey]);
        }

        return parseFloat(ladder[selectionCount]) || null;
    }
);

export const selectAccaBoostForBetStructure = createSelector(
    selectAccaBoostBonusDetails,
    selectAccaBoostPercentage,
    (bonusDetails, percentage) => {
        if (percentage === null) {
            return null;
        }

        return {
            id: bonusDetails.id,
            type_id: ACCA_BOOST_TYPE_ID,
            request: {
                boost: percentage,
            },
        };
    }
);

export const selectAccaBoostMinOddsCheck = createSelector(
    selectAccaBoostMinOddsPerLeg,
    selectSelectionDetails,
    (oddsPerLeg, selections) => {
        const minOddsCheck = Object.values(selections).some(({ decimal_price }) => {
            return (Number(decimal_price) < Number(oddsPerLeg.decimal));
        });

        return minOddsCheck;
    }
);

export const selectAccaBoostMarketsCheck = createSelector(
    selectAccaBoostMarkets,
    selectSelectionDetails,
    (markets, selections) => {
        return Object.values(selections).every(({ market_id }) => markets.includes(market_id));
    }
);

export const selectShouldShowAccaBoostMinOddsBanner = createSelector(
    selectCountSelectionDetailsWithoutCombinations,
    selectAccaBoostMinLegs,
    selectAccaBoostMarketsCheck,
    selectAccaBoostMinOddsCheck,
    (selectionCount, minLegs, isAccaBoostMarkets, isAccaBoostMinOdds) => {
        return selectionCount >= minLegs && isAccaBoostMarkets && isAccaBoostMinOdds;
    }
);

export const selectAccaBoostMinOddsFormatPerLeg = createSelector(
    selectAccaBoostBonusDetails,
    selectOddsFormat,
    ({ restrictions }, oddsFormat) => {
        switch (oddsFormat) {
            case AMERICAN_ODDS_FORMAT:
                return restrictions?.min_odds_per_leg?.american;
            case FRACTION_ODDS_FORMAT:
                return restrictions?.min_odds_per_leg?.fractional;
            default:
                return restrictions?.min_odds_per_leg?.decimal;
        }
    }
);

export const selectMarketHasAccaBoost = createSelector(
    [
        selectAccaBoostMarkets,
        (_, marketId) => marketId,
    ],
    (eligibleMarkets, marketId) => eligibleMarkets?.includes(marketId) || false
);

export const selectAccaBoostMaxPercentage = createSelector(
    selectAccaBoostLadder,
    (boostLadder) => Object.values(boostLadder).reduce((max, value) => {
        const accaPercentage = typeof value === 'number' ? value : Number(value);

        if (Number.isNaN(accaPercentage)) {
            return max;
        }

        return max === null || accaPercentage > max ? accaPercentage : max;
    }, null)
);

export const selectAccaBoostMaxReturns = createSelector(
    selectAccaBoostBonusDetails,
    selectCurrencyIso,
    ({ restrictions }, currencyCode) => Number(restrictions?.max_bonus_returns?.[currencyCode]) || null
);

export const selectAccaBoostNextPercentage = createSelector(
    selectAccaBoostLadder,
    selectAccaBoostPercentage,
    selectAccaBoostMaxPercentage,
    selectSelectionDetails,
    (ladder, accaBoostPercentage, maxPercentage, selections) => {
        const selectionCount = Object.keys(selections).length + 1;

        if (!accaBoostPercentage || accaBoostPercentage === maxPercentage) {
            return null;
        }

        return parseFloat(ladder[selectionCount]) || null;
    }
);
